import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getOrders, deleteOrder } from "../utils/orderUtils";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import CustomInput from "../components/common/CustomInput";
import OrderFilterModal from "../components/OrderFilterModal";
import {
  FunnelIcon,
  PlusIcon,
  DocumentTextIcon,
  TrashIcon,
  ClockIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    minHeight: "100vh",
    padding: "16px 24px",
    paddingTop: 16,
    maxWidth: 1400,
    margin: "0 auto",
  },
  headerContainer: {
    marginBottom: 12,
    animation: "fadeIn 0.5s ease-in-out",
  },
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  title: {
    fontSize: 32,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    fontWeight: 700,
  },
  filterButton: {
    padding: 10,
    borderRadius: 12,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  searchInput: {
    maxWidth: 300,
    width: "100%",
  },
  resultCount: {
    fontSize: 16,
    color: COLORS.gray,
    marginBottom: 12,
    fontFamily: FONTS.regularInter,
  },
  orderItem: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    marginBottom: 12,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${COLORS.tertiary}`,
    transition: "all 0.3s ease",
    animation: "fadeIn 0.5s ease-in-out",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 6px 16px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  statusLine: {
    height: 4,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  orderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
  },
  orderInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  orderIcon: {
    width: 28,
    height: 28,
    color: COLORS.primary,
  },
  orderDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  orderText: {
    fontSize: 16,
    color: COLORS.text,
    fontFamily: FONTS.regularInter,
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  statusText: {
    fontSize: 14,
    fontFamily: FONTS.bold,
  },
  statusIcon: {
    width: 20,
    height: 20,
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  deleteButton: {
    padding: 10,
    borderRadius: 12,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  addButton: {
    position: "fixed",
    bottom: 32,
    right: 32,
    border: "none",
    borderRadius: 32,
    width: 64,
    height: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.primary,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
    color: COLORS.white,
    transition: "all 0.3s ease",
    zIndex: 1000,
    "&:hover": {
      backgroundColor: COLORS.secondary,
      color: COLORS.primary,
      transform: "scale(1.1)",
      boxShadow: `0 6px 20px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.3)`,
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  emptyText: {
    fontSize: 18,
    color: COLORS.gray,
    textAlign: "center",
    marginTop: 32,
    fontFamily: FONTS.regularInter,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 32,
    fontFamily: FONTS.regularInter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingSpinner: {
    border: `3px solid ${COLORS.gray}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 24,
    height: 24,
    animation: "spin 1s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    searchInput: {
      maxWidth: "100%",
    },
    orderItem: {
      padding: 16,
    },
    actionsContainer: {
      width: "100%",
      justifyContent: "flex-end",
    },
    addButton: {
      bottom: 16,
      right: 16,
      width: 56,
      height: 56,
    },
  },
};

// Persist the Orders filters + search across navigation (e.g. opening an order
// and pressing Back). Module scope survives this screen unmounting/remounting
// within the SPA, so the chosen filter stays until the user changes it. A full
// page reload resets it. Date values stay as Date objects (no serialization).
const defaultFilters = {
  equipmentId: "",
  startDateFrom: null,
  startDateTo: null,
  endDateFrom: null,
  endDateTo: null,
  priceMin: "",
  priceMax: "",
  status: "",
};
let cachedFilters = { ...defaultFilters };
let cachedSearchQuery = "";

const OrderListScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const refresh = params.get("refresh") === "true";

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(cachedSearchQuery);
  const [isFilterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useState(cachedFilters);
  const [deletingId, setDeletingId] = useState(null);

  // Keep the module-level cache in sync so the selection survives navigation.
  useEffect(() => {
    cachedFilters = filters;
  }, [filters]);
  useEffect(() => {
    cachedSearchQuery = searchQuery;
  }, [searchQuery]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const orderList = await getOrders(user.uid);
        const validOrders = orderList.filter((order) => order.id);
        if (validOrders.length !== orderList.length) {
          console.warn("Some orders missing IDs:", orderList);
        }
        setOrders(validOrders);
        setFilteredOrders(validOrders);
      } catch (error) {
        console.error("Failed to fetch orders:", error.message, error.stack);
        alert("Failed to fetch orders: " + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
    if (refresh) {
      params.delete("refresh");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [user.uid, refresh, navigate]);

  useEffect(() => {
    let result = orders;

    if (searchQuery) {
      result = result.filter((order) =>
        order.jobName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filters.equipmentId) {
      result = result.filter(
        (order) => order.equipmentId === filters.equipmentId
      );
    }
    if (filters.startDateFrom) {
      result = result.filter(
        (order) => order.startDate >= filters.startDateFrom.toISOString()
      );
    }
    if (filters.startDateTo) {
      result = result.filter(
        (order) => order.startDate <= filters.startDateTo.toISOString()
      );
    }
    if (filters.endDateFrom) {
      result = result.filter(
        (order) =>
          order.approximateEndDate &&
          order.approximateEndDate >= filters.endDateFrom.toISOString()
      );
    }
    if (filters.endDateTo) {
      result = result.filter(
        (order) =>
          order.approximateEndDate &&
          order.approximateEndDate <= filters.endDateTo.toISOString()
      );
    }
    if (filters.priceMin) {
      result = result.filter(
        (order) => order.price >= parseFloat(filters.priceMin)
      );
    }
    if (filters.priceMax) {
      result = result.filter(
        (order) => order.price <= parseFloat(filters.priceMax)
      );
    }
    if (filters.status) {
      result = result.filter((order) => order.status === filters.status);
    }

    setFilteredOrders(result);
  }, [orders, searchQuery, filters]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "#FFA500";
      case "Active":
        return "#0000FF";
      case "Completed":
        return "#008000";
      case "Cancelled":
        return "#FF0000";
      default:
        return COLORS.text;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Pending":
        return (
          <ClockIcon
            style={styles.statusIcon}
            stroke={getStatusColor(status)}
          />
        );
      case "Active":
        return (
          <PlayIcon style={styles.statusIcon} stroke={getStatusColor(status)} />
        );
      case "Completed":
        return (
          <CheckCircleIcon
            style={styles.statusIcon}
            stroke={getStatusColor(status)}
          />
        );
      case "Cancelled":
        return (
          <XCircleIcon
            style={styles.statusIcon}
            stroke={getStatusColor(status)}
          />
        );
      default:
        return null;
    }
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    setDeletingId(id);
    try {
      await deleteOrder(id);
      setOrders(orders.filter((order) => order.id !== id));
      setFilteredOrders(filteredOrders.filter((order) => order.id !== id));
      alert("Order deleted successfully");
    } catch (error) {
      console.error("Error deleting order:", error);
      alert("Failed to delete order: " + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleNavigateToCreate = () => {
    console.log("Navigating to create order");
    navigate("/create-order/new");
  };

  const renderOrderItem = (item) => {
    if (!item.id) {
      console.warn("Order missing ID:", item);
      return null;
    }
    return (
      <div
        style={styles.orderItem}
        key={item.id}
        onClick={() => {
          console.log("Navigating to edit order:", item.id);
          navigate("/create-order/edit", { state: { order: item } });
        }}
      >
        <div
          style={{
            ...styles.statusLine,
            backgroundColor: getStatusColor(item.status),
          }}
        />
        <div style={styles.orderContent}>
          <div style={styles.orderInfo}>
            <DocumentTextIcon style={styles.orderIcon} />
            <div style={styles.orderDetails}>
              <span style={styles.orderText}>{item.equipmentName}</span>
              <span style={styles.orderText}>{item.contactName}</span>
              <span style={styles.orderText}>{item.jobName || "N/A"}</span>
              <span style={styles.orderText}>
                {moment(item.startDate).format("MM/DD/YYYY")}
              </span>
            </div>
            <div style={styles.statusContainer}>
              {getStatusIcon(item.status)}
              <span
                style={{
                  ...styles.statusText,
                  color: getStatusColor(item.status),
                }}
              >
                {item.status}
              </span>
            </div>
          </div>
          <div style={styles.actionsContainer}>
            <button
              style={{ ...styles.deleteButton }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteOrder(item.id);
              }}
              disabled={deletingId === item.id}
            >
              {deletingId === item.id ? (
                <div style={styles.loadingSpinner}></div>
              ) : (
                <TrashIcon width={24} height={24} stroke={COLORS.error} />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <div style={styles.headerContainer}>
          <div style={styles.titleContainer}>
            <h1 style={styles.title}>Orders</h1>
            <button
              style={styles.filterButton}
              onClick={() => setFilterModalVisible(true)}
            >
              <FunnelIcon width={24} height={24} stroke={COLORS.primary} />
            </button>
          </div>
          <CustomInput
            placeholder="Search by job"
            value={searchQuery}
            onChangeText={setSearchQuery}
            icon={"zoom"}
            style={styles.searchInput}
          />
          <span style={styles.resultCount}>
            Showing {filteredOrders.length} order
            {filteredOrders.length !== 1 ? "s" : ""}
          </span>
        </div>
        {loading ? (
          <div style={styles.loader}>
            <div style={styles.loadingSpinner}></div>
            <span style={styles.emptyText}>Loading orders...</span>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div style={styles.emptyText}>No orders found</div>
        ) : (
          <div>{filteredOrders.map(renderOrderItem)}</div>
        )}
        <button style={styles.addButton} onClick={handleNavigateToCreate}>
          <PlusIcon width={30} height={30} stroke={COLORS.white} />
        </button>
        <OrderFilterModal
          isVisible={isFilterModalVisible}
          onClose={() => setFilterModalVisible(false)}
          onApply={setFilters}
          currentFilters={filters}
          userId={user.uid}
        />
      </div>
    </div>
  );
};

export default OrderListScreen;
